import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import PopupMessage from '../layouts/PopupMessage';
import '../css/css_pages/PreviousReferrals.css';
import config from '../../config';

const PreviousReferrals = () => {
  const { t } = useTranslation();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const getAuthHeaders = () => {
    const token = Cookies.get('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token || ''}`
    };
  };

  const displayMessage = (msg, error = false) => {
    setMessage(msg);
    setIsError(error);
    setShowMessage(true);
    setTimeout(() => setShowMessage(false), 5000);
  };

  const fetchReferrals = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${config.API_URL}/sales-ref/get-sales-referrals-by-user`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(t('referralsFetchError'));
      }

      const data = await response.json();
      console.log('API Response:', data); // Debugging: log the API response
      
      const referralsData = Array.isArray(data) ? data.map(referral => ({
        ...referral,
        status: referral.status ? String(referral.status) : 'pending',
        // Try multiple possible date fields
        displayDate: referral.created_date || referral.created_at || referral.date_created || referral.date
      })) : [];
      setReferrals(referralsData);
    } catch (err) {
      console.error('Error fetching referrals:', err);
      setError(err.message);
      displayMessage(t('referralsLoadError'), true);
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, []);

  const getStatusBadgeClass = (status) => {
    if (!status) return 'status-badge-pending';
    
    const statusStr = String(status).toLowerCase();
    switch (statusStr) {
      case 'approved':
        return 'status-badge-approved';
      case 'pending':
        return 'status-badge-pending';
      case 'rejected':
        return 'status-badge-rejected';
      default:
        return 'status-badge-pending';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('notAvailable');
    
    try {
      // Try parsing as ISO string first
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // If ISO parsing fails, try parsing as timestamp
        const timestamp = Date.parse(dateString);
        if (isNaN(timestamp)) {
          return t('notAvailable');
        }
        date = new Date(timestamp);
      }
      
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return date.toLocaleDateString(undefined, options);
    } catch {
      return t('notAvailable');
    }
  };

  const getDisplayValue = (value) => {
    return value || t('notAvailable');
  };

  const getStatusDisplayText = (status) => {
    if (!status) return t('pending');
    const statusStr = String(status).toLowerCase();
    return t(statusStr);
  };

  if (loading) {
    return (
      <div className="previous-referrals-container">
        <div className="loading-spinner"></div>
        <div className="loading-message">{t('loadingReferrals')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="previous-referrals-container">
        <div className="error-message">
          {error}
          <button onClick={fetchReferrals} className="retry-btn">
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="previous-referrals-container">
      <div className="referrals-header">
        <h1>{t('yourReferralHistory')}</h1>
        <button
          onClick={fetchReferrals}
          className="refresh-btn"
          aria-label={t('refresh')}
          disabled={loading}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C15.0583 2 17.8308 3.35938 19.8289 5.5M19.8289 5.5V2M19.8289 5.5H16.3431" stroke="#1c3663" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {!Array.isArray(referrals) || referrals.length === 0 ? (
        <div className="no-referrals">
          <div className="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 22V12H15V22" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h3>{t('noReferralsFound')}</h3>
            <p>{t('noReferralsDescription')}</p>
          </div>
        </div>
      ) : (
        <div className="referrals-grid">
          {referrals.map((referral, index) => (
            <div key={referral.id || index} className="referral-card">
              <div className="card-header">
                <div className="society-info">
                  <h3 className="society-name">{getDisplayValue(referral.name_of_society)}</h3>
                  <span className={`status-badge ${getStatusBadgeClass(referral.status)}`}>
                    {getStatusDisplayText(referral.status)}
                  </span>
                </div>
                <div className="referral-date">{formatDate(referral.displayDate)}</div>
              </div>

              {/* Rest of the card content remains the same */}
              <div className="card-body">
                <div className="detail-row">
                  <span className="detail-label">{t('societyPhoneNumber')}:</span>
                  <span className="detail-value">{getDisplayValue(referral.main_phone_number)}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">{t('product')}:</span>
                  <span className="detail-value">{getDisplayValue(referral.product_name)}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">{t('district')}:</span>
                  <span className="detail-value">{getDisplayValue(referral.district_name)}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">{t('societyCategory')}:</span>
                  <span className="detail-value">{getDisplayValue(referral.category_name)}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">{t('societyAddress')}:</span>
                  <span className="detail-value">{getDisplayValue(referral.address)}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">{t('contactPersonName')}:</span>
                  <span className="detail-value">{getDisplayValue(referral.contact_person)}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">{t('contactPersonPhone')}:</span>
                  <span className="detail-value">{getDisplayValue(referral.contact_person_phone_number)}</span>
                </div>
              </div>

              <div className="card-footer">
                <div className="referral-id">ID: {referral.id || t('notAvailable')}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showMessage && (
        <PopupMessage
          message={message}
          onClose={() => setShowMessage(false)}
          isError={isError}
        />
      )}
    </div>
  );
};

export default PreviousReferrals;